import type {ReactElement} from 'react';
import React from 'react';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {BaseVectorColorOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-color-opacity-option';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {BaseVectorRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-roundness-option/base-vector-roundness-option';
import type {TSerializedFabricColor} from '@PosterWhiteboard/items/item/item.types';
import {convertFabricColorToColorPickerColor, getAlphaFromFabricColor, getColorPickerColorTypeFromFabricColor} from '@Utils/color.util';

interface BaseVectorColorOptionProps {
  fillObject: TSerializedFabricColor;
  onChangeFill(newFill: TSerializedFabricColor): void;
  onClickColorFill(): void;
  onChangeOpacity(updatedValue: number, undoable?: boolean): void;
  label?: string;
  showRoundnessOption?: boolean;
  fillRoundness?: number;
  onChangeRoundness?(updatedValue: number, undoable?: boolean): void;
}

export function BaseVectorColorOption({
  fillRoundness = 0,
  label = window.i18next.t('pmwjs_color'),
  showRoundnessOption = false,
  ...props
}: BaseVectorColorOptionProps): ReactElement<any> {
  const isToggled = (): boolean => {
    return !!props.fillObject;
  };

  const getColorOpacityOption = (): ReactElement<any> => {
    return (
      <BaseVectorColorOpacityOption
        fillAlpha={getAlphaFromFabricColor(props.fillObject)}
        onChangeOpacity={(val: number, undoable = true): void => {
          props.onChangeOpacity(val, undoable);
        }}
      />
    );
  };

  const getFillRoundnessOption = (): ReactElement<any> => {
    return (
      <BaseVectorRoundnessOption
        fillRoundness={fillRoundness}
        onChangeRoundness={(val: number, undoable = true): void => {
          if (props.onChangeRoundness) {
            props.onChangeRoundness(val, undoable);
          }
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (props.fillObject) {
      return (
        <>
          {getColorFillOption()}
          {getColorOpacityOption()}
          {showRoundnessOption ? getFillRoundnessOption() : null}
        </>
      );
    }
    return null;
  };

  const getColorObject = (): ColorFillStorage[] => {
    return [
      {
        id: 'color-fill-vector',
        fillType: getColorPickerColorTypeFromFabricColor(props.fillObject),
        color: convertFabricColorToColorPickerColor(props.fillObject),
      },
    ];
  };

  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const onToggleHandler = (): void => {
    if (!props.fillObject) {
      props.onChangeFill('rgba(0,0,0,1)');
    } else {
      props.onChangeFill(null);
    }
  };

  const getColorFillOption = (): ReactElement<any> => {
    return <ContextualMenuColorFillOption id="vector-fill-color-option" label={window.i18next.t('pmwjs_color')} data={getColorObject()} onClick={onClickColorFill} />;
  };

  return (
    <ContextualMenuToggleOption id="vector-color-option-toggle" label={label} isChecked={isToggled()} onClick={onToggleHandler}>
      {getSubOptions()}
    </ContextualMenuToggleOption>
  );
}
